<script setup>
import { ref, computed, watch } from 'vue'
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Eye,
  Edit2,
  Trash2,
  MapPin,
  FileText,
  Map,
  Settings,
  ChevronRight,
  ChevronDown
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty
} from '@/components/ui/table'

const props = defineProps({
  features: {
    type: Array,
    required: true
  },
  isUploading: {
    type: Boolean,
    default: false
  },
  selectedProject: {
    type: Object,
    default: null
  }
})

const emit = defineEmits([
  'preview-feature',
  'edit-feature',
  'delete-feature',
  'bulk-edit',
  'upload-selected',
  'validate-feature',
  'update-feature'
])

const sortBy = ref('name')
const sortOrder = ref('asc')
const coordinateFormat = ref('DD') // DD, DMS, DDM
const editingCoordinates = ref(new Set())
const expandedRows = ref(new Set())



// Computed properties
const filteredFeatures = computed(() => {
  // Handle empty or invalid features array
  if (!props.features || !Array.isArray(props.features) || props.features.length === 0) {
    return []
  }

  let filtered = [...props.features]

  // Sort features
  filtered.sort((a, b) => {
    let aValue, bValue

    switch (sortBy.value) {
      case 'name':
        aValue = a.properties?.name || a.properties?.fileName || ''
        bValue = b.properties?.name || b.properties?.fileName || ''
        // Use string comparison
        if (sortOrder.value === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      case 'type':
        aValue = a.geometry?.type || ''
        bValue = b.geometry?.type || ''
        // Use string comparison
        if (sortOrder.value === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      case 'file':
        aValue = a.properties?.fileName || ''
        bValue = b.properties?.fileName || ''
        // Use string comparison
        if (sortOrder.value === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      case 'status':
        aValue = getValidationStatus(a).priority
        bValue = getValidationStatus(b).priority
        // Use numeric comparison
        if (sortOrder.value === 'asc') {
          return aValue - bValue
        } else {
          return bValue - aValue
        }
      default:
        return 0
    }
  })

  return filtered
})

const validFeatures = computed(() => {
  return filteredFeatures.value.filter(feature =>
    getValidationStatus(feature).status === 'valid'
  )
})

const visibleFeatures = computed(() => {
  return filteredFeatures.value.map((feature, index) => ({ feature, index }))
})

const coordinateFormats = [
  { value: 'DD', label: 'Decimal Degrees (DD)' },
  { value: 'DMS', label: 'Degrees Minutes Seconds (DMS)' },
  { value: 'DDM', label: 'Degrees Decimal Minutes (DDM)' }
]



// Methods
const getValidationStatus = (feature) => {
  const issues = []
  const warnings = []

  // Check if feature exists and has basic structure
  if (!feature) {
    issues.push('Invalid feature structure')
    return { status: 'error', icon: XCircle, class: 'text-red-500', priority: 2, issues }
  }

  // Check geometry (required for valid feature)
  if (!feature.geometry || !feature.geometry.type) {
    issues.push('Missing geometry')
  } else {
    // Check coordinates for different geometry types
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length < 2 ||
          coords[0] === null || coords[1] === null ||
          Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90) {
        issues.push('Invalid coordinates')
      }
    } else if (feature.geometry.type === 'LineString') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length < 2) {
        issues.push('LineString requires at least 2 points')
      }
    } else if (feature.geometry.type === 'Polygon') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length === 0 || coords[0].length < 4) {
        issues.push('Polygon requires at least 4 points')
      }
    }
  }

  // Check for properties (optional - features can have null properties)
  const hasProperties = feature.properties && typeof feature.properties === 'object'

  // Check name (optional - only warning if missing when properties exist)
  if (hasProperties && !feature.properties.name && !feature.properties.fileName) {
    warnings.push('Missing name')
  }

  // Add info about description if present
  const hasDescription = hasProperties && feature.properties.description &&
    typeof feature.properties.description === 'string' &&
    feature.properties.description.trim() !== ''

  // Determine status based on issues
  if (issues.length === 0) {
    let statusText = 'Valid'
    if (hasDescription) {
      statusText = 'Valid (with description)'
    } else if (hasProperties) {
      statusText = 'Valid (with properties)'
    } else {
      statusText = 'Valid (geometry only)'
    }

    return {
      status: 'valid',
      icon: CheckCircle,
      class: 'text-green-500',
      priority: 0,
      statusText,
      warnings
    }
  } else if (issues.some(issue => issue.includes('geometry') || issue.includes('coordinates') || issue.includes('points'))) {
    return { status: 'error', icon: XCircle, class: 'text-red-500', priority: 2, issues, warnings }
  } else {
    return { status: 'warning', icon: AlertTriangle, class: 'text-yellow-500', priority: 1, issues, warnings }
  }
}

const getGeometryIcon = (type) => {
  switch (type) {
    case 'Point': return MapPin
    case 'LineString': return Edit2
    case 'Polygon': return Map
    default: return FileText
  }
}

const formatCoordinates = (feature) => {
  if (!feature.geometry || !feature.geometry.coordinates) return '-'

  const coords = feature.geometry.coordinates
  if (feature.geometry.type !== 'Point') {
    return `${coords.length} points`
  }

  const [lng, lat] = coords

  switch (coordinateFormat.value) {
    case 'DMS':
      return `${formatDMS(lat, 'lat')}, ${formatDMS(lng, 'lng')}`
    case 'DDM':
      return `${formatDDM(lat, 'lat')}, ${formatDDM(lng, 'lng')}`
    default: // DD
      return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`
  }
}

const formatDMS = (decimal, type) => {
  const abs = Math.abs(decimal)
  const degrees = Math.floor(abs)
  const minutes = Math.floor((abs - degrees) * 60)
  const seconds = ((abs - degrees) * 60 - minutes) * 60

  const direction = type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W')

  return `${degrees}°${minutes}'${seconds.toFixed(2)}"${direction}`
}

const formatDDM = (decimal, type) => {
  const abs = Math.abs(decimal)
  const degrees = Math.floor(abs)
  const minutes = (abs - degrees) * 60

  const direction = type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W')

  return `${degrees}°${minutes.toFixed(4)}'${direction}`
}

const handleSort = (field) => {
  if (sortBy.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = field
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (field) => {
  if (sortBy.value !== field) return ''
  return sortOrder.value === 'asc' ? '↑' : '↓'
}

const startCoordinateEdit = (feature, index) => {
  const key = getFeatureKey(feature, index)
  editingCoordinates.value.add(key)
}

const stopCoordinateEdit = (feature, index) => {
  const key = getFeatureKey(feature, index)
  editingCoordinates.value.delete(key)
  // Validate and update coordinates
  const coords = feature.geometry.coordinates
  if (coords && coords.length >= 2) {
    // Trigger validation
    emit('validate-feature', feature)
  }
}

const updateCoordinates = (feature, lat, lng) => {
  if (feature.geometry.type === 'Point') {
    feature.geometry.coordinates = [Number(lng), Number(lat)]
    emit('update-feature', feature)
  }
}



// Row expansion methods
const toggleRowExpansion = (feature, index) => {
  const key = getFeatureKey(feature, index)
  if (expandedRows.value.has(key)) {
    expandedRows.value.delete(key)
  } else {
    expandedRows.value.add(key)
  }
}

const isRowExpanded = (feature, index) => {
  const key = getFeatureKey(feature, index)
  return expandedRows.value.has(key)
}

const formatFeatureData = (feature) => {
  const hasProperties = feature.properties && typeof feature.properties === 'object'

  const data = {
    'Basic Information': {
      'Name': hasProperties ? (feature.properties.name || feature.properties.fileName || `${feature.geometry?.type || 'Feature'} (Unnamed)`) : `${feature.geometry?.type || 'Feature'} (No properties)`,
      'Description': hasProperties ? (
        typeof feature.properties.description === 'string' ? feature.properties.description :
        feature.properties.description ? JSON.stringify(feature.properties.description) : 'No description'
      ) : 'No properties',
      'File Source': hasProperties ? (feature.properties.fileName || 'Unknown') : 'Unknown',
      'Local UUID': hasProperties ? (feature.properties.local_uuid || 'N/A') : 'N/A',
      'Color': hasProperties ? (feature.properties.color || '#0059ff') : '#0059ff',
      'Shared': hasProperties ? (feature.properties.shared ? 'Yes' : 'No') : 'No',
      'Static': hasProperties ? (feature.properties.is_static ? 'Yes' : 'No') : 'No',
      'Project ID': hasProperties ? (feature.properties.project_id || 'Not assigned') : 'Not assigned'
    },
    'Geometry Information': {
      'Type': feature.geometry?.type || 'Unknown',
      'Coordinates': formatGeometryCoordinates(feature.geometry),
      'Coordinate Count': getCoordinateCount(feature.geometry)
    },
    'Validation Status': {
      'Status': getValidationStatus(feature).status,
      'Issues': getValidationStatus(feature).issues?.join(', ') || 'None',
      'Warnings': getValidationStatus(feature).warnings?.join(', ') || 'None'
    }
  }

  // Add all other properties if they exist
  if (hasProperties) {
    data['All Properties'] = {}
    Object.keys(feature.properties).forEach(key => {
      if (!['name', 'description', 'fileName', 'local_uuid', 'color', 'shared', 'is_static', 'project_id', 'isEditing'].includes(key)) {
        data['All Properties'][key] = feature.properties[key]
      }
    })
  }

  return data
}

const formatGeometryCoordinates = (geometry) => {
  if (!geometry || !geometry.coordinates) return 'N/A'

  switch (geometry.type) {
    case 'Point':
      return `[${geometry.coordinates[0].toFixed(6)}, ${geometry.coordinates[1].toFixed(6)}]`
    case 'LineString':
      return `${geometry.coordinates.length} points`
    case 'Polygon':
      return `${geometry.coordinates[0].length} points (outer ring)`
    case 'MultiPoint':
      return `${geometry.coordinates.length} points`
    case 'MultiLineString':
      return `${geometry.coordinates.length} lines`
    case 'MultiPolygon':
      return `${geometry.coordinates.length} polygons`
    case 'GeometryCollection':
      return `${geometry.geometries.length} geometries`
    default:
      return 'Complex geometry'
  }
}

const getCoordinateCount = (geometry) => {
  if (!geometry || !geometry.coordinates) return 0

  switch (geometry.type) {
    case 'Point':
      return 1
    case 'LineString':
      return geometry.coordinates.length
    case 'Polygon':
      return geometry.coordinates.reduce((sum, ring) => sum + ring.length, 0)
    case 'MultiPoint':
      return geometry.coordinates.length
    case 'MultiLineString':
      return geometry.coordinates.reduce((sum, line) => sum + line.length, 0)
    case 'MultiPolygon':
      return geometry.coordinates.reduce((sum, polygon) =>
        sum + polygon.reduce((polySum, ring) => polySum + ring.length, 0), 0
      )
    case 'GeometryCollection':
      return geometry.geometries.reduce((sum, geom) => sum + getCoordinateCount(geom), 0)
    default:
      return 0
  }
}

// Helper function to get a unique key for a feature
const getFeatureKey = (feature, index) => {
  if (feature.properties?.local_uuid) {
    return feature.properties.local_uuid
  }

  // For features without properties, create a key based on geometry and index
  if (feature.geometry) {
    const geomType = feature.geometry.type
    const coords = JSON.stringify(feature.geometry.coordinates)
    return `${geomType}-${index}-${coords.substring(0, 50)}`
  }

  return `feature-${index}`
}

// Watch for changes
watch(() => props.features, () => {
  // Features changed, no need to clear selection since we removed it
}, { deep: true })


</script>

<template>
  <div class="space-y-4">
    <!-- Header with stats -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <div class="text-sm text-gray-600">
          {{ filteredFeatures.length }} features
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <Button
          v-if="validFeatures.length > 0"
          @click="emit('upload-selected')"
          size="sm"
          :disabled="isUploading || !selectedProject"
          :title="!selectedProject ? 'Select a project first' : ''"
        >
          Upload All
          ({{ validFeatures.length }})
          <span v-if="selectedProject" class="ml-1 text-xs opacity-75">
            → {{ selectedProject.project_name }}
          </span>
        </Button>
      </div>
    </div>

    <!-- Controls Row -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <!-- <div class="flex items-center space-x-2">
          <Settings class="w-4 h-4 text-gray-500" />
          <Select v-model="coordinateFormat">
            <SelectTrigger class="w-48">
              <SelectValue placeholder="Coordinate format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="format in coordinateFormats"
                :key="format.value"
                :value="format.value"
              >
                {{ format.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div> -->
      </div>
    </div>

    <!-- Summary badges -->
    <div class="flex items-center space-x-2">
      <Badge variant="secondary" class="text-green-600">
        <CheckCircle class="w-3 h-3 mr-1" />
        {{ validFeatures.length }} Valid
      </Badge>
      <Badge variant="secondary" class="text-yellow-600">
        <AlertTriangle class="w-3 h-3 mr-1" />
        {{ filteredFeatures.filter(f => getValidationStatus(f).status === 'warning').length }} Warnings
      </Badge>
      <Badge variant="secondary" class="text-red-600">
        <XCircle class="w-3 h-3 mr-1" />
        {{ filteredFeatures.filter(f => getValidationStatus(f).status === 'error').length }} Errors
      </Badge>
    </div>

            <!-- Table -->
    <Table class="border rounded-lg">
      <TableHeader>
        <TableRow>
          <TableHead
            class="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
            @click="handleSort('name')"
          >
            Name {{ getSortIcon('name') }}
          </TableHead>
          <TableHead
            class="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
            @click="handleSort('type')"
          >
            Type {{ getSortIcon('type') }}
          </TableHead>
          <TableHead>Coordinates</TableHead>
          <TableHead
            class="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
            @click="handleSort('file')"
          >
            File {{ getSortIcon('file') }}
          </TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        <template v-if="filteredFeatures.length > 0">
          <template v-for="{ feature, index } in visibleFeatures" :key="getFeatureKey(feature, index)">
            <TableRow class="hover:bg-gray-50">
              <!-- Name -->
              <TableCell class="min-w-0">
                <div class="flex flex-col justify-center">
                  <div class="font-medium flex items-center space-x-2 truncate max-w-20">
                    <span>
                      {{
                        feature.properties?.name ||
                        feature.properties?.fileName ||
                        `${feature.geometry?.type || 'Feature'} ${feature.properties ? '(Unnamed)' : '(No properties)'}`
                      }}
                    </span>
                  </div>
                  <div v-if="!feature.properties" class="text-sm text-gray-400 truncate">
                    Geometry only
                  </div>
                </div>
              </TableCell>

              <!-- Type -->
              <TableCell>
                <div class="flex items-center space-x-2">
                  <component
                    :is="getValidationStatus(feature).icon"
                    :class="getValidationStatus(feature).class"
                    class="w-3.5 h-3.5"
                    :title="getValidationStatus(feature).issues?.join(', ') || 'Valid'"
                  />
                  <span class="text-sm">{{ feature.geometry?.type || 'Unknown' }}</span>
                </div>
              </TableCell>

              <!-- Coordinates -->
              <TableCell>
                <div class="flex items-center">
                  <div
                    v-if="feature.geometry?.type === 'Point' && editingCoordinates.has(getFeatureKey(feature, index))"
                    class="flex items-center space-x-1"
                  >
                    <Input
                      :value="feature.geometry.coordinates[1]"
                      @input="(e) => updateCoordinates(feature, e.target.value, feature.geometry.coordinates[0])"
                      class="w-20 h-6 text-xs"
                      placeholder="Lat"
                    />
                    <Input
                      :value="feature.geometry.coordinates[0]"
                      @input="(e) => updateCoordinates(feature, feature.geometry.coordinates[1], e.target.value)"
                      class="w-20 h-6 text-xs"
                      placeholder="Lng"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      @click="stopCoordinateEdit(feature, index)"
                      class="h-6 w-6 p-0"
                    >
                      ✓
                    </Button>
                  </div>
                  <div
                    v-else
                    class="flex items-center space-x-1"
                  >
                    <code
                      class="text-xs bg-gray-100 px-1 py-0.5 rounded cursor-pointer"
                      @click="feature.geometry?.type === 'Point' ? startCoordinateEdit(feature, index) : null"
                      :title="feature.geometry?.type === 'Point' ? 'Click to edit' : ''"
                    >
                      {{ formatCoordinates(feature) }}
                    </code>
                    <Button
                      v-if="feature.geometry?.type === 'Point'"
                      size="sm"
                      variant="ghost"
                      @click="startCoordinateEdit(feature, index)"
                      class="h-6 w-6 p-0"
                    >
                      <Edit2 class="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </TableCell>

              <!-- Source File -->
              <TableCell>
                <div class="text-sm text-gray-600 truncate max-w-20">
                  {{ feature.properties?.fileName || 'Unknown' }}
                </div>
              </TableCell>

              <!-- Actions -->
              <TableCell>
                <div class="flex items-center space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    @click="emit('preview-feature', feature)"
                    class="h-8 w-8 p-0"
                    title="Preview on map"
                  >
                    <Eye class="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    @click="emit('delete-feature', feature)"
                    class="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    title="Delete feature"
                  >
                    <Trash2 class="w-4 h-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    @click="toggleRowExpansion(feature, index)"
                    class="h-8 w-8 p-0"
                    title="Show details"
                  >
                    <ChevronRight
                      v-if="!isRowExpanded(feature, index)"
                      class="w-4 h-4"
                    />
                    <ChevronDown
                      v-else
                      class="w-4 h-4"
                    />
                  </Button>
                </div>
              </TableCell>
            </TableRow>

            <!-- Expanded Details Row -->
            <TableRow
              v-if="isRowExpanded(feature, index)"
              class="bg-gray-50 border-t"
            >
              <TableCell colspan="6" class="p-4">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div
                    v-for="(section, sectionName) in formatFeatureData(feature)"
                    :key="sectionName"
                    class="space-y-2"
                  >
                    <h4 class="font-semibold text-gray-800 border-b pb-1">
                      {{ sectionName }}
                    </h4>
                    <div class="space-y-1">
                      <div
                        v-for="(value, key) in section"
                        :key="key"
                        class="flex justify-between"
                      >
                        <span class="text-gray-600">{{ key }}:</span>
                        <span class="font-medium text-right ml-2 break-words">
                          {{ typeof value === 'object' ? JSON.stringify(value) : value }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </template>
        </template>

        <!-- Empty state -->
        <TableEmpty v-else colspan="6">
          <div class="text-center py-12">
            <FileText class="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 class="text-lg font-medium text-gray-900 mb-2">No features found</h3>
            <p class="text-gray-500">
              Import some files to get started.
            </p>
          </div>
        </TableEmpty>
      </TableBody>
    </Table>

  </div>
</template>
